import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigationTabs } from '../../components/NavigationTabs/NavigationTabs';
import { BackButton } from '../../components/BackButton/BackButton';
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator';
import { fetchPurchasedProducts } from '../../api/productApi';
import { Product } from '../../types/product';
import styles from './MyPurchases.module.scss';

export const MyPurchases: React.FC = () => {
    const navigate = useNavigate();
    const [purchasedProducts, setPurchasedProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'products' | 'favorites' | 'account'>('account');

    useEffect(() => {
        const loadPurchasedProducts = async () => {
            try {
                const products = await fetchPurchasedProducts();
                setPurchasedProducts(products);
            } catch (err) {
                setError('Не удалось загрузить список покупок');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        loadPurchasedProducts();
    }, []);

    const handleTabChange = (tab: 'products' | 'favorites' | 'account') => {
        setActiveTab(tab);
        if (tab === 'products') navigate('/');
        else if (tab === 'favorites') navigate('/favorites');
        else if (tab === 'account') navigate('/account');
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <LoadingIndicator message="Загрузка покупок..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.errorContainer}>
                <BackButton />
                <div className={styles.error}>
                    <p>{error}</p>
                    <button
                        className={styles.retryButton}
                        onClick={() => window.location.reload()}
                    >
                        Попробовать снова
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles['my-purchases']}>
            <BackButton className={styles['backButton']} />

            <header className={styles["purchases-header"]}>
                <h1 className={styles["purchases-title"]}>Мои покупки</h1>
                <span className={styles["purchases-count"]}>{purchasedProducts.length}</span>
            </header>

            <main className={styles["purchases-content"]}>
                {purchasedProducts.length > 0 ? (
                    <div className={styles["purchases-grid"]}>
                        {purchasedProducts.map(product => (
                            <article key={product.id} className={styles["course-card"]}>
                                <div className={styles["course-card__media"]}>
                                    <img
                                        src={product.image}
                                        alt={product.title}
                                        className={styles["course-card__image"]}
                                        loading="lazy"
                                    />
                                </div>
                                <div className={styles["course-card__content"]}>
                                    <h2 className={styles["course-card__title"]}>{product.title}</h2>
                                    <p className={styles["course-card__meta"]}>{product.videosCount} видео</p>
                                    {product.author && (
                                        <div className={styles["course-card__author"]}>
                                            <span className={styles["author-label"]}>Автор</span>
                                            <span className={styles["author-name"]}>{product.author}</span>
                                        </div>
                                    )}
                                    <button
                                        className={styles["course-card__button"]}
                                        onClick={() => navigate(`/my-purchase/${product.id}`)}
                                    >
                                        Смотреть курс
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                ) : (
                    <div className={styles["empty-state"]}>
                        <p>У вас пока нет покупок</p>
                    </div>
                )}
            </main>

            <footer className={styles["purchases-footer"]}>
                <NavigationTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </footer>
        </div>
    );
};